import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isMobile } from '../../../screenUtils';
import { analyticsTrack } from '../../../analyticsUtils';
import { putRequest } from '../../../requestUtils';
import LazyFinish from './LazyFinish';
import './Finish.scss';
import { processedProfileSuggestions } from '../onboardingUtils';

const resourceString = (resourceLink) => {
  if (resourceLink.includes('project') || resourceLink.includes('proposal')) {
    return 'view my opportunity';
  }
  return 'view open opportunities';
};

const bodyString = (isFinished) => {
  if (isFinished) {
    return (
      <p className="body-text-1">
        Do not forget to
        {' '}
        <b>complete 100%</b>
        {' '}
        of your profile to receive better project
        recommendations and rank higher when searching for candidates.
      </p>
    );
  }
  return (
    <p className="body-text-1">
      Adding to your profile will ensure that Clora understands your
      preferences and experience, offering you better suggestions and results..
    </p>
  );
};

function Finish({
  userId, resourceLink, csrfToken, name, finish,
  step, onboardType, percentage, claimInfoUrl, closeModal,
}) {
  const {
    profileSuggestions,
    errors: errorsProfileSuggestions,
  } = useSelector((state) => state.profileSuggestions);

  const onSaveAndExit = () => {
    let data;

    analyticsTrack('Exiting from Finish Later', {
      location: 'Onboarding Flow',
      step,
      userId,
    });

    if (step === 'Start Here') {
      if (errorsProfileSuggestions.is) {
        return;
      }

      data = processedProfileSuggestions(profileSuggestions.merged);
    }

    putRequest(
      claimInfoUrl,
      { claim_info: data },
      csrfToken,
    )
      .then(() => {
        window.history.pushState({}, '', resourceLink);
        window.location.reload();
      });
  };

  const onProfileUrl = () => {
    analyticsTrack('Clicking View my clora profile', {
      location: 'Onboarding Flow',
      step,
      userId,
    });
  };

  // acts as a stand-in for full onboarding, sort of
  if (name) {
    return (
      <div className="style-guide finish-container onboarding-finish-modal">
        <h1 className="welcome-greeting mb-3">
          {finish ? 'It is time to explore opportunities!' : 'Are you sure you want to exit now?'}
        </h1>
        {bodyString(finish, percentage)}

        <div className={`d-flex ${finish ? 'g-row--between' : ' finish-later flex-column flex-md-row justify-content-center gap-2'}`}>
          {!finish
            && (
            <Fragment>
              <a
                tabIndex={0}
                role="button"
                className={`btn btn-primary onboard-save-and-exit-btn ${isMobile() ? 'mb-3' : ''}`}
                id="onboard-save-and-exit"
                onClick={() => onSaveAndExit()}
              >
                Save and Exit
              </a>
              <a
                tabIndex={0}
                role="button"
                className="btn btn-primary onboard-add-to-profile-btn"
                id="onboard-view-my-profile"
                onClick={() => closeModal()}
              >
                Cancel
              </a>
            </Fragment>
            )}
          {finish
            && (
            <Fragment>
              <a
                className="btn btn-secondary text-capitalize finish-resource-btn"
                id="onboard-view-my-profile"
                onClick={() => onProfileUrl()}
                href={`/users/${userId}/onboard`}
              >
                view my clora profile
              </a>
              <a
                className={'btn btn-primary text-capitalize '
                + 'onboard-resource-link finish-resource-btn'}
                id="onboard-resource-link"
                href={resourceLink}
              >
                {resourceString(resourceLink)}
              </a>
            </Fragment>
            )}
        </div>
      </div>
    );
  }
  return (
    <LazyFinish
      csrfToken={csrfToken}
      onboardType={onboardType}
    />
  );
}

Finish.propTypes = {
  resourceLink: PropTypes.string.isRequired,
  name: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  finish: PropTypes.bool.isRequired,
  onboardType: PropTypes.string.isRequired,
  percentage: PropTypes.number,
  step: PropTypes.string.isRequired,
  userId: PropTypes.string,
  claimInfoUrl: PropTypes.string,
  closeModal: PropTypes.func,
};

Finish.defaultProps = {
  claimInfoUrl: null,
  name: null,
  percentage: null,
  userId: null,
};

export default Finish;
